import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import { ChevronRight, Plus, UserPlus, Users, X } from 'lucide-react';
import { SPACES_QUERY } from '../graphql/queries';
import {
  ADD_SPACE_MEMBER_MUTATION,
  CREATE_SPACE_MUTATION,
  REMOVE_SPACE_MEMBER_MUTATION,
} from '../graphql/mutations';
import { FetchErrorCard } from '../components/FetchErrorCard';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';

interface Member {
  id: string;
  email: string;
  displayName: string | null;
}

interface Space {
  id: string;
  name: string;
  slug: string;
  members: Member[];
}

interface SpacesData {
  spaces: Space[];
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Derives a URL/folder-safe slug from a human space name ("Family Trips" → "family-trips"). */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Shared spaces: the caller's spaces lead the page; creating a new one lives
 * behind a button (design review B3). No developer vocabulary up front — the
 * folder slug is auto-derived from the name and only editable under
 * "Advanced".
 */
export function SpacesPage() {
  const { data, loading, error } = useQuery<SpacesData>(SPACES_QUERY);
  const [creating, setCreating] = useState(false);
  const spaces = data?.spaces ?? [];
  const loaded = Boolean(data);

  const showForm = creating || (loaded && spaces.length === 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 px-1 pt-4 sm:pt-6 pb-2">
        <div>
          <h1 className="text-2xl font-display font-semibold text-[var(--text-primary)]">
            Shared spaces
          </h1>
          <p className="mt-1 text-[var(--text-secondary)]">
            A space is a set of documents your group holds together — a family grocery list, a
            shared journal.
          </p>
        </div>
        {loaded && spaces.length > 0 && !creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn btn-primary text-sm gap-2 shrink-0"
          >
            <Plus className="w-4 h-4" aria-hidden /> New space
          </button>
        )}
      </div>

      {showForm && (
        <CreateSpaceForm
          onDone={() => setCreating(false)}
          showCancel={spaces.length > 0}
          firstSpace={spaces.length === 0}
        />
      )}

      {error && !data ? (
        <FetchErrorCard>
          Something went wrong loading your spaces. Try again in a moment.
        </FetchErrorCard>
      ) : loading && !data ? (
        <div className="space-y-2.5" aria-hidden="true">
          <span className="skeleton block h-24 w-full rounded-xl" />
          <span className="skeleton block h-24 w-full rounded-xl" />
        </div>
      ) : (
        spaces.map((space) => <SpaceCard key={space.id} space={space} />)
      )}
    </div>
  );
}

type CreateError = 'slug_format' | 'slug_taken' | 'generic' | null;

interface CreateSpaceFormProps {
  /** Called after a successful create, and by Cancel. */
  onDone: () => void;
  showCancel: boolean;
  /** First space: warmer copy, since the form IS the empty state. */
  firstSpace: boolean;
}

/** Form to create a new shared space (name; slug auto-derived, Advanced-only). */
function CreateSpaceForm({ onDone, showCancel, firstSpace }: CreateSpaceFormProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [errorKind, setErrorKind] = useState<CreateError>(null);

  const [createSpace, { loading }] = useMutation(CREATE_SPACE_MUTATION, {
    refetchQueries: ['Spaces', 'Me'],
    awaitRefetchQueries: true,
  });

  const effectiveSlug = slugEdited ? slug.trim() : slugify(name);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugEdited) setSlug(slugify(value));
    if (errorKind) setErrorKind(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName || !effectiveSlug) return;
    if (!SLUG_PATTERN.test(effectiveSlug)) {
      setErrorKind('slug_format');
      setAdvancedOpen(true);
      return;
    }
    setErrorKind(null);
    try {
      await createSpace({ variables: { name: trimmedName, slug: effectiveSlug } });
      setName('');
      setSlug('');
      setSlugEdited(false);
      onDone();
    } catch (err) {
      const code = (err as ApolloError).graphQLErrors?.[0]?.extensions?.code;
      setErrorKind(code === 'BAD_USER_INPUT' ? 'slug_taken' : 'generic');
      if (code === 'BAD_USER_INPUT') setAdvancedOpen(true);
    }
  };

  return (
    <section
      aria-label="Create a space"
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft"
    >
      <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
        {firstSpace ? 'Create your first space' : 'Create a space'}
      </h2>
      {firstSpace && (
        <p className="text-sm text-[var(--text-tertiary)] mb-3">
          Name it after the people who'll share it — Family, Roommates, The Band.
        </p>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="space-name" className="label">
            Name
          </label>
          <input
            id="space-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Family"
            className="mt-1"
          />
        </div>

        <div>
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            aria-expanded={advancedOpen}
            className={`inline-flex min-h-11 items-center gap-1 text-sm font-medium text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors ${FOCUS_RING_CLASSES}`}
          >
            <ChevronRight
              className={`w-4 h-4 transition-transform ${advancedOpen ? 'rotate-90' : ''}`}
              aria-hidden
            />
            Advanced
          </button>
          {advancedOpen && (
            <div className="mt-1">
              <label htmlFor="space-slug" className="label">
                Folder name
              </label>
              <input
                id="space-slug"
                type="text"
                value={slugEdited ? slug : effectiveSlug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugEdited(true);
                  if (errorKind) setErrorKind(null);
                }}
                placeholder="family"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                aria-invalid={
                  errorKind === 'slug_format' || errorKind === 'slug_taken' ? true : undefined
                }
                className="mt-1 font-mono text-sm"
              />
              <p className="help mt-1">
                Where the space's documents live, e.g. {`${effectiveSlug || 'family'}/lists/…`} —
                lowercase letters, numbers and dashes.
              </p>
            </div>
          )}
        </div>

        {errorKind === 'slug_format' && (
          <p role="alert" className="text-sm text-[var(--text-primary)]">
            Folder names use lowercase letters, numbers and dashes only (like {`family-trips`}).
          </p>
        )}
        {errorKind === 'slug_taken' && (
          <p role="alert" className="text-sm text-[var(--text-primary)]">
            That folder name is already taken. Try another under Advanced.
          </p>
        )}
        {errorKind === 'generic' && (
          <div
            role="alert"
            className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl text-sm"
          >
            The space wasn't created. Something went wrong on our end — try again.
          </div>
        )}

        <div className="flex justify-end gap-2">
          {showCancel && (
            <button type="button" onClick={onDone} className="btn btn-ghost" disabled={loading}>
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !name.trim() || !effectiveSlug}
          >
            {loading ? 'Creating…' : 'Create space'}
          </button>
        </div>
      </form>
    </section>
  );
}

/** One space: its members and the add/remove member controls. */
function SpaceCard({ space }: { space: Space }) {
  const [email, setEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const [addMember, { loading: adding }] = useMutation(ADD_SPACE_MEMBER_MUTATION, {
    refetchQueries: ['Spaces'],
    awaitRefetchQueries: true,
  });
  const [removeMember] = useMutation(REMOVE_SPACE_MEMBER_MUTATION, {
    refetchQueries: ['Spaces'],
    awaitRefetchQueries: true,
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setAddError(null);
    try {
      await addMember({ variables: { spaceId: space.id, email: trimmed } });
      setEmail('');
    } catch (err) {
      const code = (err as ApolloError).graphQLErrors?.[0]?.extensions?.code;
      setAddError(
        code === 'NOT_FOUND'
          ? 'No one with that email was found.'
          : "That didn't work. Check the email and try again."
      );
    }
  };

  const handleRemove = (userId: string) => {
    void removeMember({ variables: { spaceId: space.id, userId } });
  };

  return (
    <section
      aria-label={space.name}
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
    >
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-[var(--text-tertiary)]" aria-hidden />
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">{space.name}</h2>
        <span className="text-xs text-[var(--text-tertiary)]">
          {space.members.length} {space.members.length === 1 ? 'member' : 'members'}
        </span>
      </div>

      <ul className="space-y-1 mb-4">
        {space.members.map((member) => {
          const memberName = member.displayName || member.email;
          const initial = memberName.charAt(0).toUpperCase();
          return (
            <li
              key={member.id}
              className="flex items-center gap-3 rounded-xl px-3 py-2 bg-[color-mix(in_oklab,var(--text-primary)_4%,transparent)]"
            >
              <span
                aria-hidden
                className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--marine-400)] to-[var(--marine-600)] flex items-center justify-center text-white text-sm font-bold shrink-0"
              >
                {initial}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-[var(--text-primary)]">
                  {memberName}
                </span>
                {member.displayName && (
                  <span className="block truncate text-xs text-[var(--text-tertiary)]">
                    {member.email}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(member.id)}
                className={`min-h-11 min-w-11 inline-flex items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-medium text-[var(--text-tertiary)] hover:text-[var(--danger)] hover:bg-[color-mix(in_oklab,var(--danger)_8%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
                aria-label={`Remove ${memberName}`}
              >
                <X className="w-4 h-4" aria-hidden />
                <span className="hidden sm:inline">Remove</span>
              </button>
            </li>
          );
        })}
      </ul>

      <form onSubmit={handleAdd} className="flex items-end gap-2">
        <div className="flex-1">
          <label htmlFor={`add-member-${space.id}`} className="label">
            Add member
          </label>
          <input
            id={`add-member-${space.id}`}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (addError) setAddError(null);
            }}
            placeholder="name@example.com"
            className="mt-1"
          />
        </div>
        <button type="submit" className="btn btn-primary gap-2" disabled={adding || !email.trim()}>
          <UserPlus className="w-4 h-4" aria-hidden /> Add
        </button>
      </form>
      {addError && (
        <p role="alert" className="mt-2 text-sm text-[var(--text-primary)]">
          {addError}
        </p>
      )}
    </section>
  );
}
